import React from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {useTheme} from '@mui/material/styles';
import {
    SxProps,
    Grid,
    Box,
    Stack,
    Avatar,
    Typography,
    Link,
    CardContent,
} from '@mui/material';
import MainCard from '../../components/MainCard';
import {TextLoading, TextRequestFailed} from './misc';
import useSummary from '../../queries/useSummary';
import TimeAgo from '../../components/TimeAgo';

import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import PaidIcon from '@mui/icons-material/Paid';
import GrainIcon from '@mui/icons-material/Grain';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import ReceiptIcon from '@mui/icons-material/Receipt';

type StatCardProps = {
    children?: React.ReactNode;
    sx?: SxProps;
};

const StatCard = ({children, sx}: StatCardProps) => {
    return (
        <Box
            sx={{
                height: 1,
                display: 'flex',
                boxShadow: 3,
                borderRadius: 4,
                ...sx,
            }}
        >
            {children}
        </Box>
    );
};

const makeStatAvatar = (IconComponent: any) => {
    return (
        <Avatar sx={{mx: 2, width: '60', height: '60', backgroundColor: 'inherit'}}>
            <IconComponent sx={{width: '40px', height: '40px', opacity: 0.8}} color="primary"/>
        </Avatar>
    );
};

type GeneralCardProps = {
    theme: any;
    avatar: any,
    title: string;
    content: React.ReactNode;
    footnote: string | React.ReactNode;
};

const SubCard = ({theme, avatar, title, content, footnote}: GeneralCardProps) => {
    return (
        <Stack direction="row" sx={{flexGrow: 1, width: 1}}>
            <Box>
                {makeStatAvatar(avatar)}
            </Box>
            <Box>
                <Typography variant="h5" sx={{width: 1}}>
                    {title.toUpperCase()}
                </Typography>
                {content}
                <Typography sx={{display: 'inline', fontSize: '0.7em', color: 'gray'}}>
                    {footnote}
                </Typography>
            </Box>
        </Stack>
    );
};

const OverviewContent = () => {
    const theme = useTheme();
    const summaryQuery = useSummary();
    if (summaryQuery.isLoading) {
        return TextLoading;
    }
    if (summaryQuery.isError) {
        return TextRequestFailed;
    }
    const summary = summaryQuery.data.payload;

    return (
        <Grid container spacing={{xs: 1, sm: 2, md: 3}} columns={12} sx={{backgroundColor: 'inherit'}}>
            <Grid item xs={12} sm={6} md={4} sx={{height: {xs: 1, sm: 100, md: 120}}}>
                <StatCard sx={{pt: {xs: 1.5, md: 2}}}>
                    <SubCard theme={theme}
                             avatar={LocalFireDepartmentIcon}
                             title={'LATEST BLOCK'}
                             content={
                                 <Typography>
                                     <Link component={RouterLink} to={`/block/${summary.latestBlockHeight}`}>
                                         {summary.latestBlockHeight}
                                     </Link>
                                     {' '} in {`${summary.latestBlockTime} secs`}
                                 </Typography>
                             }
                             footnote={
                                 <TimeAgo date={summary.latestBlockTimestamp * 1000}/>
                             }
                    />
                </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{height: {xs: 1, sm: 100, md: 120}}}>
                <StatCard sx={{pt: {xs: 1.5, md: 2}}}>
                    <SubCard theme={theme}
                             avatar={ReceiptIcon}
                             title={'TOTAL LEDGER SIZE'}
                             content={
                                 <Typography sx={{color: theme.palette.primary.main}}>
                                     {`${Math.round(summary.totalFullSize / 1024 / 1024).toLocaleString()} MB`}
                                 </Typography>
                             }
                             footnote={`${summary.totalFullSize.toLocaleString()} bytes`}
                    />
                </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{height: {xs: 1, sm: 100, md: 120}}}>
                <StatCard sx={{pt: {xs: 1.5, md: 2}}}>
                    <SubCard theme={theme}
                             avatar={PaidIcon}
                             title={'TOTAL NUMBER OF TRANSACTIONS'}
                             content={
                                 <Typography sx={{color: theme.palette.primary.main}}>
                                     <Link component={RouterLink} to={'/txs'}>
                                         {`${summary.totalTxs.toLocaleString()}`}
                                     </Link>
                                 </Typography>
                             }
                             footnote={'    '}
                    />
                </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{height: {xs: 1, sm: 100, md: 120}}}>
                <StatCard sx={{pt: {xs: 1.5, md: 2}}}>
                    <SubCard theme={theme}
                             avatar={AccessAlarmIcon}
                             title={'AVERAGE BLOCK TIME'}
                             content={
                                 <Typography sx={{color: theme.palette.primary.main}}>
                                     {`${summary.avgBlockTimeLast1k.toFixed(1)} secs`}
                                 </Typography>
                             }
                             footnote={'over latest 1,000 blocks'}
                    />
                </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{height: {xs: 1, sm: 100, md: 120}}}>
                <StatCard sx={{pt: {xs: 1.5, md: 2}}}>
                    <SubCard theme={theme}
                             avatar={GrainIcon}
                             title={'AVERAGE NETWORK HASH RATE'}
                             content={
                                 <Typography sx={{color: theme.palette.primary.main}}>
                                     {`${Number((summary.networkHashRateLast1K / 10 ** 9).toFixed(2)).toLocaleString()} GH/s`}
                                 </Typography>
                             }
                             footnote={'over latest 1,000 blocks'}
                    />
                </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{height: {xs: 1, sm: 100, md: 120}}}>
                <StatCard sx={{pt: {xs: 1.5, md: 2}}}>
                    <SubCard theme={theme}
                             avatar={AccountBalanceWalletIcon}
                             title={'TOTAL NUMBER OF ADDRESSES'}
                             content={
                                 <Typography sx={{color: theme.palette.primary.main}}>
                                     <Link component={RouterLink} to={'/addresses'}>
                                         {`${summary.totalAddress.toLocaleString()}`}
                                     </Link>
                                 </Typography>
                             }
                             footnote='    '
                    />
                </StatCard>
            </Grid>
        </Grid>
    );
};
const Overview = () => {
    return (
        <MainCard title="Overview">
            <CardContent sx={{maxHeight: 520, overflow: 'auto'}}>
                <OverviewContent/>
            </CardContent>
        </MainCard>
    );
};
export default Overview;
